import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { ChangeFileStateApiService } from '../../api/change-file-state-api.service';
import {
  EditStateDialogData,
  EditStateDialogResult
} from '../../model/edit-state-dialog.model';

const STATES_REQUIRING_MOTIVE = new Set(['ANULADO', 'POSTERGADO']);

@Component({
  selector: 'app-edit-state',
  templateUrl: './edit-state.component.html',
  styleUrls: ['./edit-state.component.css']
})
export class EditStateComponent implements OnInit {
  usuario = '';
  isLoading = false;
  selectedTarget = '';
  motive = '';

  constructor(
    private readonly changeFileStateApi: ChangeFileStateApiService,
    public dialogRef: MatDialogRef<EditStateComponent, EditStateDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: EditStateDialogData
  ) {}

  ngOnInit(): void {
    this.usuario = this.getUserFullName();
    this.selectedTarget = this.nextStates[0] || this.data?.targetState || this.data?.state || '';
  }

  get currentState(): string {
    return (
      this.data?.currentState ||
      this.data?.itinerario?.displayState ||
      'SIN ESTADO'
    );
  }

  get targetState(): string {
    return this.selectedTarget;
  }

  get nextStates(): string[] {
    if (this.data?.operationsNextStates?.length) {
      return this.data.operationsNextStates;
    }

    const fallback = this.data?.targetState || this.data?.state;
    return fallback ? [fallback] : [];
  }

  get hasMultipleTargets(): boolean {
    return this.nextStates.length > 1;
  }

  get contextLabel(): string {
    return this.data?.context === 'PROGRAMACION' ? 'Programación' : 'File';
  }

  get sourceArea(): string {
    return this.data?.sourceArea || 'OPERATIONS';
  }

  get hasStateChangeAction(): boolean {
    return !!this.data?.fileId && !!this.targetState;
  }

  get requiresMotive(): boolean {
    return STATES_REQUIRING_MOTIVE.has(this.targetState);
  }

  get hasValidMotive(): boolean {
    return this.motive.trim().length > 0;
  }

  get canConfirm(): boolean {
    if (!this.hasStateChangeAction || this.isLoading) return false;
    if (this.requiresMotive && !this.hasValidMotive) return false;
    return true;
  }

  get fileLabel(): string {
    const itinerary = this.data?.itinerario;
    const fileNumber = [itinerary?.fileNumber, itinerary?.fileExtend]
      .filter(Boolean)
      .join('-');
    const fileTitle = itinerary?.nombreItinerario || 'File seleccionado';

    return fileNumber ? `${fileNumber} · ${fileTitle}` : fileTitle;
  }

  get salesNextStates(): string[] {
    return this.data?.salesNextStates ?? [];
  }

  get hasSalesReference(): boolean {
    return this.salesNextStates.length > 0;
  }

  get automaticTransitions(): Array<{
    fromState: string;
    toState: string;
    description: string;
  }> {
    return this.data?.automaticTransitions ?? [];
  }

  get hasAutomaticReference(): boolean {
    return this.automaticTransitions.length > 0;
  }

  selectTarget(state: string): void {
    if (this.isLoading || state === this.selectedTarget) return;

    this.selectedTarget = state;
    if (!this.requiresMotive) {
      this.motive = '';
    }
  }

  save(): void {
    if (!this.canConfirm) return;

    this.isLoading = true;
    this.showProcessingAlert();

    this.changeFileStateApi
      .changeProgrammingFileState({
        fileId: this.data.fileId as string,
        targetState: this.targetState,
        userName: this.usuario
      })
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          Swal.close();

          if (response.changeEstadoItinerarioByArg === true) {
            this.dialogRef.close({
              updated: true,
              targetState: this.targetState,
              response
            });
            this.showSuccessAlert();
            return;
          }

          this.showUnexpectedResponseAlert();
        },
        error: (error) => {
          this.isLoading = false;
          this.showErrorAlert(error);
        }
      });
  }

  cancelar(): void {
    if (this.isLoading) return;
    this.dialogRef.close();
  }

  private getUserFullName(): string {
    return [localStorage.getItem('nombre'), localStorage.getItem('apellido')]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  private showProcessingAlert(): void {
    void Swal.fire({
      title: 'Actualizando estado',
      html: `
        <div style="text-align:left;color:#475569;font-size:14px;line-height:1.45">
          <div style="margin-bottom:12px;padding:12px 14px;border-radius:14px;background:#fff7ed;border:1px solid #fed7aa">
            <div style="color:#9a3412;font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase">Operations · Programación</div>
            <div style="margin-top:6px;color:#1f2937;font-weight:700">${this.escapeHtml(this.currentState)} → ${this.escapeHtml(this.targetState)}</div>
          </div>
          Estamos registrando el cambio con tu usuario. No cierres esta ventana.
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  private showSuccessAlert(): void {
    void Swal.fire({
      icon: 'success',
      title: 'Estado actualizado',
      html: `
        <div style="text-align:left;color:#475569;font-size:14px;line-height:1.45">
          <div style="margin-bottom:12px;padding:14px;border-radius:14px;background:#ecfdf5;border:1px solid #bbf7d0">
            <div style="color:#15803d;font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase">Cambio registrado</div>
            <div style="margin-top:8px;color:#064e3b;font-size:16px;font-weight:800">${this.escapeHtml(this.targetState)}</div>
          </div>
          <div style="color:#64748b">${this.escapeHtml(this.fileLabel)}</div>
        </div>
      `,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#16a34a',
      timer: 3200,
      timerProgressBar: true,
      width: 460
    });
  }

  private showUnexpectedResponseAlert(): void {
    void Swal.fire({
      icon: 'warning',
      title: 'No se confirmó el cambio',
      html: `
        <div style="text-align:left;color:#475569;font-size:14px;line-height:1.45">
          No pudimos confirmar que el estado se haya guardado correctamente.
          <div style="margin-top:12px;padding:12px;border-radius:12px;background:#fff7ed;border:1px solid #fed7aa;color:#9a3412">
            Estado que intentaste guardar: <strong>${this.escapeHtml(this.targetState)}</strong>
          </div>
        </div>
      `,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#e95a1a',
      width: 480
    });
  }

  private showErrorAlert(error: unknown): void {
    void Swal.fire({
      icon: 'error',
      title: 'No se pudo actualizar',
      html: `
        <div style="text-align:left;color:#475569;font-size:14px;line-height:1.45">
          <div style="margin-bottom:12px;padding:14px;border-radius:14px;background:#fef2f2;border:1px solid #fecaca">
            <div style="color:#b91c1c;font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase">Cambio no registrado</div>
            <div style="margin-top:8px;color:#7f1d1d;font-weight:700">${this.escapeHtml(this.currentState)} → ${this.escapeHtml(this.targetState)}</div>
          </div>
          ${this.escapeHtml(this.getErrorMessage(error))}
        </div>
      `,
      confirmButtonText: 'Reintentar',
      confirmButtonColor: '#dc2626',
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      reverseButtons: true,
      width: 500
    }).then((result) => {
      if (result.isConfirmed) {
        this.save();
      }
    });
  }

  private getErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message || 'Ocurrió un error inesperado.');
    }

    return 'Ocurrió un error inesperado. Verifica tu conexión o intenta nuevamente.';
  }

  private escapeHtml(value: string | number): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
